import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types';

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => setCurrent((v) => (v + 1) % items.length), 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center bg-background-100">
        <p className="text-foreground-500">暂无焦点内容，欢迎浏览下方推荐影片</p>
      </div>
    );
  }

  const go = (dir: number) => setCurrent((v) => (v + dir + items.length) % items.length);

  return (
    <section className="relative w-full h-[440px] md:h-[620px] overflow-hidden bg-background-100">
      {items.map((movie, idx) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${idx === current ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} - 午夜电影网焦点推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-screen mx-auto">
            <div className="max-w-xl" key={current}>
              <div className="flex items-center gap-2 mb-3 animate-fade-up">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold font-label tracking-wider">
                  TOP {current + 1}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-200/80 text-foreground-800 text-xs">
                  独家焦点
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-black text-foreground-950 leading-tight mb-2 animate-fade-up">
                {items[current].title}
              </h1>
              <p className="text-sm md:text-base text-foreground-700 mb-4 animate-fade-up">
                {items[current].subtitle}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-foreground-700 mb-5">
                <span className="flex items-center gap-1 text-primary-400 font-semibold">
                  <i className="ri-star-fill"></i> {items[current].rating.toFixed(1)}
                </span>
                <span>{items[current].year}</span>
                <span>{items[current].region}</span>
                <span>{items[current].genres.join(' / ')}</span>
              </div>
              <p className="hidden sm:block text-sm text-foreground-600 leading-relaxed mb-6 line-clamp-2 max-w-lg">
                {items[current].synopsis}
              </p>
              <div className="flex items-center gap-3">
                <Link
                  to={`/movie/${items[current].id}`}
                  className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg"></i> 查看详情
                </Link>
                <Link
                  to="/movies"
                  className="px-5 py-2.5 rounded-md border border-background-400/60 hover:border-primary-500 text-foreground-900 text-sm font-medium transition-colors whitespace-nowrap"
                >
                  浏览全部影片
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-4 md:left-8 z-20 flex items-center gap-3">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一部"
          className="w-9 h-9 rounded-full bg-background-200/70 hover:bg-primary-500 text-foreground-900 hover:text-background-50 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一部"
          className="w-9 h-9 rounded-full bg-background-200/70 hover:bg-primary-500 text-foreground-900 hover:text-background-50 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
        <div className="flex items-center gap-2 ml-2">
          {items.map((m, idx) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setCurrent(idx)}
              aria-label={`切换到第${idx + 1}部`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${idx === current ? 'w-6 bg-primary-500' : 'w-1.5 bg-background-500'}`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}